import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Star, Award, TrendingUp, Shield, Users, Home, Navigation, Palette } from "lucide-react";
import CTABand from "@/components/recruit/CTABand";

// 3 Core Values - ACOEの3つの強み
const CORE_VALUES = [
  {
    icon: Navigation,
    titleEn: 'LOCATION',
    title: 'ロケーション',
    description: '家族にやさしいエリアを厳選。主要観光エリアへのアクセスが良く、夜は静かで安心して歩ける環境。最寄り駅から徒歩圏内、スーパー・コンビニも近くにある生活利便性を重視。',
    features: [
      '主要観光エリアへのアクセス良好',
      '静かで治安の良いエリア',
      'ベビーカーでも移動しやすい導線',
      '生活利便施設が徒歩圏内'
    ],
    gradient: 'from-emerald-500 to-teal-600',
    bgGradient: 'from-emerald-50 to-teal-50',
  },
  {
    icon: Palette,
    titleEn: 'DESIGN',
    title: 'デザイン',
    description: 'ミニマルな和モダンと各エリアのローカル要素を融合した「フュージョンコンセプト」。家族が自然と集まる「ボンディングエリア」を中心に、本物感と居心地の良さを両立した空間設計。',
    features: [
      '考え抜かれた機能性: 直感的な動線・効率的なレイアウト',
      'プレミアム設備・高品質な家電とアメニティ',
      '子どもと高齢者への安全配慮',
      'プライバシーと静けさを重視'
    ],
    gradient: 'from-amber-500 to-orange-600',
    bgGradient: 'from-amber-50 to-orange-50',
  },
  {
    icon: Star,
    titleEn: 'REVIEW',
    title: 'レビュー',
    description: '「基本の徹底」が生み出す圧倒的なゲスト満足度。清掃品質の自社コントロール、5分以内のメッセージ返信、24時間コンシェルジュサービスで、市場平均を大きく上回る評価を獲得。',
    features: [
      '総合評価 4.96/5（Airbnb）',
      '総合品質スコア 96.9%（市場平均85.1%）',
      'Booking.com 平均スコア 9.2',
      'メッセージ平均返信時間 約4分'
    ],
    gradient: 'from-violet-500 to-purple-600',
    bgGradient: 'from-violet-50 to-purple-50',
  },
];

const PROOF_CARDS = [
  { value: "4.96", label: "Airbnb 平均評価（2025年）", sub: "市場平均 4.8", icon: Star },
  { value: "9.5", label: "Booking.com 評価", sub: "ACOE Asakusa", icon: Star },
  { value: "96.9%", label: "総合品質スコア", sub: "市場平均 85.1%", icon: Award },
  { value: "250%", label: "収益達成保証", sub: "長期賃貸比・60日以内", icon: TrendingUp },
  { value: "70%+", label: "GOP（営業利益率）", sub: "業界高水準", icon: Shield },
  { value: "~5万", label: "SNSフォロワー数", sub: "ブランド認知力", icon: Users },
];

const PROPERTIES = [
  { name: "ACOE Hatsudai", img: "https://s3.eu-central-1.amazonaws.com/assets.brease.io/media/sit-9f937fca-b3cf-4da3-8745-c6f91aa09f99/a0124f1d-e8be-4338-bae2-5fb36b0d9aa6.webp" },
  { name: "ACOE Nakano-Sakaue", img: "https://s3.eu-central-1.amazonaws.com/assets.brease.io/media/sit-9f937fca-b3cf-4da3-8745-c6f91aa09f99/a0124f34-31e6-4303-bfd2-ec31fe38edfe.webp" },
  { name: "ACOE Morishita", img: "https://s3.eu-central-1.amazonaws.com/assets.brease.io/media/sit-9f937fca-b3cf-4da3-8745-c6f91aa09f99/a01253a4-47b9-424d-b833-e581c1e7ed37.webp" },
  { name: "ACOE Okubo", img: "https://s3.eu-central-1.amazonaws.com/assets.brease.io/media/sit-9f937fca-b3cf-4da3-8745-c6f91aa09f99/a0125603-ed52-4f27-8839-938c9a323db3.webp" },
  { name: "ACOE Higashi-Shinjuku", img: "https://s3.eu-central-1.amazonaws.com/assets.brease.io/media/sit-9f937fca-b3cf-4da3-8745-c6f91aa09f99/a0125289-f488-4235-a4a2-59e9fb48e433.webp" },
  { name: "ACOE Asakusa", img: "https://s3.eu-central-1.amazonaws.com/assets.brease.io/media/sit-9f937fca-b3cf-4da3-8745-c6f91aa09f99/a01253ff-1903-491d-8ef3-665b879e46cf.webp" },
];

const COMPANY_INFO = [
  { label: "会社名", value: "株式会社セイライグループジャパン" },
  { label: "代表取締役", value: "シリカン アサワ" },
  { label: "設立", value: "2019年8月1日" },
  { label: "資本金", value: "851万円" },
  { label: "所在地", value: "東京都千代田区富士見2-10-2 飯田橋グラン・ブルーム9F" },
  { label: "事業内容", value: "ライフスタイル宿泊ブランドの運営 / 宿泊施設の企画・運営代行 / フランチャイズ展開" },
  { label: "公式サイト", value: "acoe.jp / acoe-partners.jp", isLink: true },
];

export default function AboutPage() {
  useEffect(() => { document.title = "ACOEについて | ACOE 採用サイト"; }, []);

  return (
    <div data-testid="about-page">
      {/* ─── HERO ─── */}
      <section className="pt-32 md:pt-40 pb-20 md:pb-28 bg-acoe-charcoal" data-testid="about-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-gold font-heading font-semibold mb-6">About ACOE</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.15] max-w-4xl">
            東京発、ファミリーのための<br />ライフスタイル宿泊ブランド
          </h1>
          <p className="mt-8 text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">
            一般的な民泊より高品質で、ホテルよりスペースがあり、完全にプライベートな空間。
            ACOEは家族や多世代グループに向けた、デザイン性の高い宿泊体験を提供しています。
          </p>
        </div>
      </section>

      {/* ─── MISSION / VISION ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="about-mission">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-24">
            <div>
              <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Mission</p>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-acoe-charcoal mb-6">
                家族が一緒に思い出を作るための<br />空間を提供する
              </h2>
              <p className="text-acoe-text leading-relaxed">
                家族にとって最高の思い出は、快適で安心できる空間から生まれる。
                だからACOEは「Basics Done Well」— 清掃、コミュニケーション、空間デザインという宿泊の基本を、妥協なく高いレベルで徹底しています。
                さらに最新のテクノロジーを活用し「より良く、便利な滞在」を実現。
                派手な演出ではなく、家族が心からくつろげる空間づくりに集中することが、
                ゲストの満足とオーナーの収益の両方につながると信じています。
              </p>
            </div>
            <div>
              <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Vision</p>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-acoe-charcoal mb-6">
                最も愛されるファミリー向け<br />ホスピタリティブランドへ
              </h2>
              <p className="text-acoe-text leading-relaxed">
                7年間の運営で培ったノウハウとブランド力を、パートナーモデルで拡大する。
                それがACOEの次のフェーズです。物件オーナーにはデータに裏打ちされた収益改善を、
                ゲストには記憶に残る滞在体験を届け続けます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── TWO CUSTOMERS ─── */}
      <section className="py-20 md:py-32 bg-acoe-paper" data-testid="about-customers">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Who We Serve</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            ACOEの2つの顧客
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white border border-acoe-border p-8 md:p-12" data-testid="customer-guest">
              <Home size={24} className="text-acoe-gold mb-6" strokeWidth={1.5} />
              <h3 className="font-heading font-bold text-xl text-acoe-charcoal mb-4">ゲスト — 家族・多世代グループ</h3>
              <p className="text-acoe-text leading-relaxed mb-4">
                子連れ家族、三世代旅行、グループ旅行者が主なターゲット。
                ホテルでは得られない広さとプライバシー、民泊にはない品質と安心感を求めるゲストに支持されています。
              </p>
              <p className="text-sm text-acoe-muted">国内外からの旅行者 / 平均宿泊2-4泊 / リピーター多数</p>
            </div>
            <div className="bg-white border border-acoe-border p-8 md:p-12" data-testid="customer-owner">
              <Shield size={24} className="text-acoe-gold mb-6" strokeWidth={1.5} />
              <h3 className="font-heading font-bold text-xl text-acoe-charcoal mb-4">物件オーナー — 収益改善パートナー</h3>
              <p className="text-acoe-text leading-relaxed mb-4">
                東京23区内の小規模物件を所有する個人・法人オーナー。
                長期賃貸以上の収益を求めるが、運営ノウハウやブランド力がないというオーナーにACOEのパートナーシップを提供します。
              </p>
              <p className="text-sm text-acoe-muted">個人オーナー / 不動産法人 / ファンド・REIT / 資産活用を検討中の方</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SERVICE MODEL ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="about-services">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Service Model</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            2つの契約モデル
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-acoe-charcoal p-8 md:p-12" data-testid="service-sublease">
              <p className="text-acoe-gold text-sm tracking-[0.2em] uppercase font-heading font-semibold mb-4">Model A</p>
              <h3 className="font-heading font-bold text-2xl text-white mb-4">サブリース</h3>
              <p className="text-white/60 leading-relaxed mb-6">
                ACOEが物件を借り上げ、自社ブランドで運営。オーナーには毎月固定の賃料を保証。
                運営リスクはACOEが負い、オーナーは安定収入を得られます。
              </p>
              <div className="h-px bg-white/10 mb-4" />
              <p className="text-sm text-white/40">オーナーリスク: 最小 / 収益: 固定賃料</p>
            </div>
            <div className="bg-acoe-charcoal p-8 md:p-12" data-testid="service-management">
              <p className="text-acoe-gold text-sm tracking-[0.2em] uppercase font-heading font-semibold mb-4">Model B</p>
              <h3 className="font-heading font-bold text-2xl text-white mb-4">運営代行契約</h3>
              <p className="text-white/60 leading-relaxed mb-6">
                オーナーの物件をACOEブランドで運営代行。手数料は売上の20%（レベニューシェア）。
                セットアップ費用はマークアップなしの実費ベース。60日以内に長期賃貸比250%の収益を目標とします。
              </p>
              <div className="h-px bg-white/10 mb-4" />
              <p className="text-sm text-white/40">オーナーリスク: 中 / 収益: 売上連動（上振れ余地あり）</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROOF CARDS ─── */}
      <section className="py-20 md:py-32 bg-acoe-paper" data-testid="about-proof">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Track Record</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            実績で証明する運営力
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {PROOF_CARDS.map((card, i) => {
              const Icon = card.icon;
              return (
                <div key={i} className="bg-white border border-acoe-border p-6 md:p-8 group hover:border-acoe-charcoal transition-all duration-300" data-testid={`proof-card-${i}`}>
                  <Icon size={20} className="text-acoe-muted mb-4 group-hover:text-acoe-gold transition-colors" strokeWidth={1.5} />
                  <p className="font-heading font-extrabold text-2xl md:text-3xl text-acoe-charcoal leading-none">{card.value}</p>
                  <p className="mt-2 text-sm text-acoe-text font-medium">{card.label}</p>
                  <p className="mt-1 text-xs text-acoe-gold">{card.sub}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── 3 CORE VALUES - ACOEの3つの強み ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="about-core-values">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">
              Our Competitive Moat
            </p>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-4">
              ACOEの3つの強み
            </h2>
            <p className="text-acoe-text max-w-2xl mx-auto">
              この3つの柱が、誰にも真似できない競争優位性を構築し、
              日本で最も愛されるファミリー向け宿泊ブランドへの道を切り開きます
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {CORE_VALUES.map((core, index) => {
              const Icon = core.icon;
              return (
                <div
                  key={index}
                  className={`relative rounded-2xl overflow-hidden bg-gradient-to-br ${core.bgGradient} 
                    border border-acoe-border shadow-sm hover:shadow-xl transition-all duration-500 group`}
                  data-testid={`core-value-${core.titleEn.toLowerCase()}`}
                >
                  {/* Header with gradient */}
                  <div className={`bg-gradient-to-r ${core.gradient} p-6 text-white`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-white/80 text-xs tracking-[0.15em] uppercase">{core.titleEn}</p>
                        <h4 className="text-xl font-medium">{core.title}</h4>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <p className="text-acoe-text text-sm leading-relaxed mb-5">
                      {core.description}
                    </p>
                    
                    {/* Features list */}
                    <ul className="space-y-2">
                      {core.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-start gap-2 text-sm text-acoe-muted">
                          <Shield className="w-4 h-4 text-acoe-gold flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Decorative corner */}
                  <div className="absolute top-0 right-0 w-20 h-20 opacity-10">
                    <div className={`absolute inset-0 bg-gradient-to-br ${core.gradient} rounded-bl-full`} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Moat statement */}
          <div className="mt-12 text-center">
            <div className="inline-block bg-acoe-charcoal text-white px-8 py-4 rounded-xl shadow-lg">
              <p className="text-lg font-light font-heading">
                <span className="text-acoe-gold">Location × Design × Review</span> = 
                <span className="font-medium ml-2">競争優位性</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── PROPERTY GALLERY ─── */}
      <section className="py-20 md:py-32 bg-acoe-paper" data-testid="about-gallery">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Our Properties</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            運営物件
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {PROPERTIES.map((prop, i) => (
              <div key={i} className="group relative aspect-[4/3] overflow-hidden" data-testid={`property-${i}`}>
                <img src={prop.img} alt={prop.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-acoe-charcoal/0 group-hover:bg-acoe-charcoal/40 transition-colors duration-300 flex items-end">
                  <p className="text-white text-sm font-heading font-semibold px-4 pb-4 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                    {prop.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-acoe-muted text-center">
            東京23区内を中心に展開中 ・
            <a href="https://www.acoe.jp/" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-acoe-charcoal transition-colors">
              全物件を見る
            </a>
          </p>
        </div>
      </section>

      {/* ─── COMPANY INFO ─── */}
      <section className="py-20 md:py-32 bg-acoe-paper" data-testid="about-company-info">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Company</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            会社概要
          </h2>
          <div className="bg-white border border-acoe-border">
            {COMPANY_INFO.map((row, i) => (
              <div key={i} className={`flex flex-col md:flex-row ${i < COMPANY_INFO.length - 1 ? "border-b border-acoe-border" : ""}`}>
                <div className="w-full md:w-48 bg-acoe-paper px-6 py-4 flex-shrink-0">
                  <p className="text-sm font-heading font-semibold text-acoe-charcoal">{row.label}</p>
                </div>
                <div className="px-6 py-4 flex-1">
                  {row.isLink ? (
                    <div className="flex gap-4">
                      <a href="https://www.acoe.jp/" target="_blank" rel="noopener noreferrer" className="text-sm text-acoe-charcoal underline underline-offset-4 hover:text-acoe-gold transition-colors">acoe.jp</a>
                      <a href="https://acoe-partners.jp/" target="_blank" rel="noopener noreferrer" className="text-sm text-acoe-charcoal underline underline-offset-4 hover:text-acoe-gold transition-colors">acoe-partners.jp</a>
                    </div>
                  ) : (
                    <p className="text-sm text-acoe-text">{row.value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <CTABand variant="dark" />
    </div>
  );
}
